/**
 * DesktopHero — pencere yokken desktop'ta gosterilir
 * Mockup v3 referans: buyuk baslik + input + skill pills + live agent strip
 * Kutu/dashboard DEGIL — tam ekran, OS ruhu
 */
import axios from 'axios'
import {
    BarChart2, Bot, Briefcase, Code2, FileText,
    FlaskConical, Globe, MessageSquare, Search,
    Zap
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { API_URL } from '../../../config/api'
import { useAuth } from '../../../contexts/AuthContext'
import AskRunAIWindow from '../AskRunAI/AskRunAIWindow'
import { useWindowManager } from '../WindowManager/WindowManagerContext'

// ─── Types ────────────────────────────────────────────────────────────────────

interface LiveAgent {
  id: number
  agent_name: string
  status: string
  initials: string
  color: string
}

interface Stats {
  agents: number
  skills: number
  flz: number
}

// ─── Skill pills ─────────────────────────────────────────────────────────────

const SKILL_PILLS = [
  { label: 'Web Scrape',   icon: <Globe size={10} />,       prompt: 'Scrape and summarize this website: ' },
  { label: 'Data Analysis',icon: <BarChart2 size={10} />,   prompt: 'Analyze this data and give insights: ' },
  { label: 'Content Gen',  icon: <FileText size={10} />,    prompt: 'Write a blog post about: ' },
  { label: 'Code Review',  icon: <Code2 size={10} />,       prompt: 'Review this code and suggest improvements: ' },
  { label: 'Research',     icon: <Search size={10} />,      prompt: 'Research and summarize: ' },
  { label: 'Email',        icon: <MessageSquare size={10} />,prompt: 'Write a professional email about: ' },
]

// ─── Agent color map ──────────────────────────────────────────────────────────

const AGENT_COLORS = [
  'linear-gradient(135deg,#ec4899,#f43f5e)',
  'linear-gradient(135deg,#6366f1,#8b5cf6)',
  'linear-gradient(135deg,#10b981,#059669)',
  'linear-gradient(135deg,#f59e0b,#d97706)',
  'linear-gradient(135deg,#06b6d4,#0891b2)',
  'linear-gradient(135deg,#a855f7,#7c3aed)',
]

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function DesktopHero() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { openWindow } = useWindowManager()
  const inputRef = useRef<HTMLInputElement>(null)

  const [input, setInput] = useState('')
  const [agents, setAgents] = useState<LiveAgent[]>([])
  const [stats, setStats] = useState<Stats>({ agents: 0, skills: 0, flz: 0 })

  useEffect(() => {
    const token = localStorage.getItem('token') ?? ''
    const hdr = { Authorization: `Bearer ${token}` }

    // Agents
    axios.get(`${API_URL}/api/agents`, { headers: hdr })
      .then(r => {
        const arr = Array.isArray(r.data) ? r.data : (r.data?.agents ?? [])
        const live: LiveAgent[] = arr.slice(0, 6).map((a: { id: number; agent_name: string; status: string }, i: number) => ({
          id: a.id,
          agent_name: a.agent_name,
          status: a.status,
          initials: a.agent_name.slice(0, 2).toUpperCase(),
          color: AGENT_COLORS[i % AGENT_COLORS.length],
        }))
        setAgents(live)
        setStats(s => ({ ...s, agents: arr.length }))
      })
      .catch(() => undefined)

    // Skills count
    axios.get(`${API_URL}/api/aicloud/skills/installed`, { headers: hdr })
      .then(r => {
        const arr = Array.isArray(r.data) ? r.data : (r.data?.skills ?? [])
        setStats(s => ({ ...s, skills: arr.length }))
      })
      .catch(() => undefined)

    // FLZ
    axios.get(`${API_URL}/api/flz/balance`, { headers: hdr })
      .then(r => {
        setStats(s => ({ ...s, flz: r.data?.balance ?? 0 }))
      })
      .catch(() => undefined)
  }, [])

  const handleRun = () => {
    const text = input.trim()
    if (!text) { return }
    // AskRunAI penceresini ac, input'u query param olarak gonder
    openWindow({
      id: 'ask',
      title: 'Ask RunAI',
      component: AskRunAIWindow,
      position: { x: Math.max(40, window.innerWidth / 2 - 360), y: 40 },
      size: { width: 720, height: 580 },
    })
    // Input'u temizle
    setInput('')
  }

  const handlePill = (prompt: string) => {
    setInput(prompt)
    inputRef.current?.focus()
  }

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { handleRun() }
  }

  const greeting = () => {
    const h = new Date().getHours()
    if (h < 12) { return 'Good morning' }
    if (h < 18) { return 'Good afternoon' }
    return 'Good evening'
  }

  return (
    <div style={{
      position: 'absolute', inset: 0,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '0 24px',
      // Hafif radyal glow — mockup gibi
      background: 'transparent',
    }}>

      {/* Greeting */}
      <div style={{
        fontSize: 13, color: 'var(--os-muted)',
        marginBottom: 8, letterSpacing: '0.3px',
      }}>
        {greeting()}, {user?.display_name ?? user?.username ?? 'there'}
      </div>

      {/* Big title */}
      <div style={{
        fontSize: 32, fontWeight: 800,
        background: 'linear-gradient(135deg,#6366f1,#ec4899)',
        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        marginBottom: 6, textAlign: 'center', lineHeight: 1.2,
      }}>
        What can I help you with?
      </div>

      {/* Subtitle stats */}
      <div style={{
        fontSize: 11, color: 'var(--os-dim)',
        marginBottom: 24, display: 'flex', gap: 12, alignItems: 'center',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Bot size={10} color="#10b981" />
          {stats.agents} agents
        </span>
        <span style={{ color: 'var(--os-border)' }}>·</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Zap size={10} color="#f59e0b" />
          {stats.skills} skills
        </span>
        <span style={{ color: 'var(--os-border)' }}>·</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ color: '#10b981', fontWeight: 600 }}>⚡</span>
          {stats.flz.toFixed(0)} FLZ
        </span>
        <span style={{ color: 'var(--os-border)' }}>·</span>
        <span>Multi-cloud · Live</span>
      </div>

      {/* Input */}
      <div style={{
        width: '100%', maxWidth: 640,
        position: 'relative', marginBottom: 12,
      }}>
        <input
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Analyze competitor websites and create a comparison report..."
          style={{
            width: '100%',
            padding: '14px 110px 14px 18px',
            background: 'var(--os-input-bg)',
            border: '1.5px solid var(--os-border)',
            borderRadius: 14,
            color: 'var(--os-text)',
            fontSize: 13,
            outline: 'none',
            fontFamily: 'inherit',
            transition: 'border .2s',
            backdropFilter: 'blur(8px)',
          }}
          onFocus={e => { e.target.style.borderColor = 'rgba(99,102,241,0.5)' }}
          onBlur={e => { e.target.style.borderColor = 'var(--os-border)' }}
        />
        <div style={{
          position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
          display: 'flex', gap: 4,
        }}>
          <button
            onClick={() => navigate('/dashboard/chat')}
            style={{
              padding: '7px 8px', background: 'transparent',
              border: '1px solid var(--os-border)', borderRadius: 8,
              cursor: 'pointer', display: 'flex', alignItems: 'center',
              color: 'var(--os-dim)',
            }}
            title="Open Chat"
          >
            <MessageSquare size={12} />
          </button>
          <button
            onClick={handleRun}
            style={{
              padding: '7px 14px',
              background: input.trim() ? '#6366f1' : 'rgba(99,102,241,0.15)',
              color: input.trim() ? '#fff' : 'var(--os-dim)',
              border: 'none', borderRadius: 8,
              fontSize: 11, fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 4,
              transition: 'all .15s',
            }}
          >
            <Zap size={11} /> Run
          </button>
        </div>
      </div>

      {/* Skill pills */}
      <div style={{
        display: 'flex', gap: 5, flexWrap: 'wrap',
        justifyContent: 'center', maxWidth: 640, marginBottom: 28,
      }}>
        {SKILL_PILLS.map(p => (
          <button
            key={p.label}
            onClick={() => handlePill(p.prompt)}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '5px 11px',
              background: 'var(--os-input-bg)',
              border: '1px solid var(--os-border)',
              borderRadius: 8,
              color: 'var(--os-dim)',
              fontSize: 10, cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'all .15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'rgba(99,102,241,0.4)'
              e.currentTarget.style.color = 'var(--os-text)'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'var(--os-border)'
              e.currentTarget.style.color = 'var(--os-dim)'
            }}
          >
            {p.icon} {p.label}
          </button>
        ))}
      </div>

      {/* Live agent strip */}
      {agents.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 14px',
          background: 'rgba(0,0,0,0.2)',
          backdropFilter: 'blur(12px)',
          border: '1px solid var(--os-border)',
          borderRadius: 12,
          maxWidth: 640, width: '100%',
        }}>
          {/* Stacked avatars */}
          <div style={{ display: 'flex' }}>
            {agents.slice(0, 5).map((a, i) => (
              <div
                key={a.id}
                title={a.agent_name}
                style={{
                  width: 24, height: 24, borderRadius: 7,
                  background: a.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 8, fontWeight: 700, color: '#fff',
                  border: '2px solid var(--os-bg, #0b0e14)',
                  marginLeft: i === 0 ? 0 : -6,
                  zIndex: 5 - i,
                  position: 'relative',
                }}
              >
                {a.initials}
              </div>
            ))}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, color: 'var(--os-text)', fontWeight: 600 }}>
              {agents.filter(a => a.status === 'active' || a.status === 'running').length} agents active
            </div>
            <div style={{ fontSize: 9, color: 'var(--os-muted)' }}>
              {agents.slice(0, 3).map(a => a.agent_name).join(' · ')}
              {agents.length > 3 ? ` · +${agents.length - 3} more` : ''}
            </div>
          </div>

          {/* Quick actions */}
          <div style={{ display: 'flex', gap: 4 }}>
            {[
              { label: 'Agents', icon: <Bot size={10} />, path: '/dashboard/agents', color: '#ec4899' },
              { label: 'Research', icon: <FlaskConical size={10} />, path: '/dashboard/projects/research', color: '#f59e0b' },
              { label: 'Work', icon: <Briefcase size={10} />, path: '/dashboard/projects/work', color: '#06b6d4' },
            ].map(item => (
              <button
                key={item.label}
                onClick={() => navigate(item.path)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 3,
                  padding: '4px 8px',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid var(--os-border)',
                  borderRadius: 6, color: 'var(--os-dim)',
                  fontSize: 9, cursor: 'pointer', fontFamily: 'inherit',
                  transition: 'all .15s',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.color = item.color
                  e.currentTarget.style.borderColor = `${item.color}44`
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.color = 'var(--os-dim)'
                  e.currentTarget.style.borderColor = 'var(--os-border)'
                }}
              >
                {item.icon} {item.label}
              </button>
            ))}
          </div>
        </div>
      )}

    </div>
  )
}
